import React, { useState } from "react";
import {
  Typography,
  Box,
  Grid,
  TextField,
  InputAdornment,
  Paper,
  IconButton,
  Tooltip,
  Stack,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  FormControlLabel,
  Checkbox,
  Button,
  SelectChangeEvent,
  Switch,
} from "@mui/material";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { es } from "date-fns/locale";
import { Z } from "../../../theme/zIndex";
import ListaLimpiezasPactadas from "./ListaLimpiezasPactadas";
import { Sector } from "@/features/calendar/types/Sector";
import NightsStayIcon from "@mui/icons-material/NightsStay";
import KingBedIcon from "@mui/icons-material/KingBed";
import SingleBedIcon from "@mui/icons-material/SingleBed";
import { useTheme, useMediaQuery } from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import ApartmentIcon from "@mui/icons-material/Apartment";
import TabHuespedes from "../../newReservation/TabHuespedes";
import { FormData as NewReservationFormData } from "../../newReservation/types";
import { parseMonetaryValue } from "../../shared/utils/formatters";
import { CalculationService } from "../../../services/calculationService";
import { logger } from "../../../utils/logger";
import { getReservationIdentityLabel } from "../utils/reservationLabels";
import { Reservation } from "../types/Reservation";
import api from "../../../config/axios";
import { useQuery } from "@tanstack/react-query";
import { fetchFechasPactadasRegistradas } from "../../cleaning/cleaningStaffApi";
import { isInternalDepartment } from "../../calendar/utils/internalDepartments";
import { formatDateKey, parseDateOnly } from "../../shared/utils/dateUtils";
import NombreClienteFields from "../../clients/components/NombreClienteFields";

interface FormData {
  entrada: Date | null;
  salida: Date | null;
  departamento: string;
  nombre: string;
  apellido: string;
  dni: string;
  valor_total: string;
  pago: string;
  ajuste_financiero: string;
  tipo_ajuste: string;
  cantidad_huespedes: string;
  tipo_cama: string[];
  localidad: string;
  email1: string;
  email2: string;
  telefono1: string;
  telefono2: string;
  acompanantes: Array<{
    nombre: string;
    apellido: string;
    dni: string;
    edad: string;
  }>;
  afiliado_nombre: string;
  afiliado_apellido: string;
  afiliado_dni: string;
  late_checkout: boolean;
  politica_precio?: string;
  precio_personalizado?: string;
  tipo_precio_personalizado?: string;
  usar_limpiezas_manuales: boolean;
  sin_limpieza_automatica: boolean;
  dias_limpieza_pactados: string[];
  dias_limpieza_recambio: string[];
}

interface FormErrors {
  entrada?: string;
  salida?: string;
  departamento?: string;
  nombre?: string;
  apellido?: string;
  dni?: string;
  valor_total?: string;
  pago?: string;
  cantidad_huespedes?: string;
  email1?: string;
  email2?: string;
  telefono1?: string;
  telefono2?: string;
  edad?: string;
  overlap?: string;
}

interface EditReservationFormProps {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  errors: FormErrors;
  setErrors: React.Dispatch<React.SetStateAction<FormErrors>>;
  activeTab: number;
  zonas: Sector[] | null;
  selectedReservation: Reservation | null;
  handleDateChange: (date: Date | null, name: "entrada" | "salida") => void;
  handleTextChange: (
    e: React.ChangeEvent<HTMLInputElement> | SelectChangeEvent<string>
  ) => void;
  handleMarcarPagado: () => void;
  esPagado: boolean;
  saldoPendiente: number;
  isShortDesktop?: boolean;
}

const renderDepartamentoSelect = (
  formData: FormData,
  errors: FormErrors,
  zonas: Sector[] | null,
  handleTextChange: (e: React.ChangeEvent<HTMLInputElement> | SelectChangeEvent<string>) => void,
  shouldShowDepartmentOption: (depto: string) => boolean
) => (
  <FormControl
    fullWidth
    error={!!errors.departamento}
    required
    size="small"
  >
    <InputLabel id="departamento-label">Departamento</InputLabel>
    <Select
      labelId="departamento-label"
      name="departamento"
      value={formData.departamento}
      onChange={handleTextChange}
      label="Departamento"
      startAdornment={
        <InputAdornment position="start">
          <ApartmentIcon sx={{ color: "primary.main" }} />
        </InputAdornment>
      }
      MenuProps={{
        sx: {
          zIndex: Z.popover,
        },
      }}
    >
      {zonas && zonas.length > 0
        ? zonas.map((sector: Sector) => {
          const departamentosVisibles = sector.departamentos.filter(
            shouldShowDepartmentOption
          );

          if (departamentosVisibles.length === 0) return [];

          return [
            <MenuItem
              key={sector.id}
              disabled
              divider
              sx={{
                bgcolor: "action.hover",
                fontWeight: "bold",
                color: "primary.main",
              }}
            >
              Sector {sector.nombre}
            </MenuItem>,
            ...departamentosVisibles.map((depto: string) => (
              <MenuItem key={depto} value={depto} sx={{ pl: 4 }}>
                {depto}
              </MenuItem>
            )),
          ];
        })
        : [
            "01",
            "02",
            "03",
            "04",
            "05",
            "06",
            "07",
            "08",
            "09",
            "10",
            "11",
            "12A",
            "12B",
            "14",
            "15",
            "16",
            "17",
            "18",
            "19",
            "20",
            "21",
            "22",
            "23",
            "24",
            "25",
            "26",
            "27",
          ].filter(shouldShowDepartmentOption).map((depto) => (
            <MenuItem key={depto} value={depto}>
              {depto}
            </MenuItem>
          ))}
    </Select>
    {errors.departamento && (
      <FormHelperText>{errors.departamento}</FormHelperText>
    )}
  </FormControl>
);

const EditReservationForm: React.FC<EditReservationFormProps> = ({
  formData,
  setFormData,
  errors,
  setErrors,
  activeTab,
  zonas,
  selectedReservation,
  handleDateChange,
  handleTextChange,
  handleMarcarPagado,
  esPagado,
  saldoPendiente,
  isShortDesktop = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // Pactadas que el personal ya marcó: se muestran bloqueadas. Si la consulta falla,
  // no se bloquea nada acá; el backend las conserva igual al guardar.
  const { data: fechasPactadasRegistradas = [] } = useQuery({
    queryKey: ["pactadas-registradas", selectedReservation?.id],
    queryFn: () => fetchFechasPactadasRegistradas(selectedReservation!.id),
    enabled: Boolean(selectedReservation?.id),
    retry: false,
    staleTime: 60_000,
  });
  const [errorLimpiezaPactada, setErrorLimpiezaPactada] = useState<string | null>(
    null
  );
  const saldoResumenLabel =
    saldoPendiente > 0
      ? `$${saldoPendiente.toLocaleString("es-AR")}`
      : saldoPendiente < 0
      ? `A favor $${Math.abs(saldoPendiente).toLocaleString("es-AR")}`
      : "Pagado";
  const shouldShowDepartmentOption = (depto: string): boolean =>
    !isInternalDepartment(depto) || formData.departamento === depto;

  // El cliente maestro es la fuente de verdad: si ya tiene el dato cargado,
  // el campo se muestra de solo lectura en la edición de reserva. Solo se
  // puede completar si está vacío. Corrección de datos existentes -> Clientes.
  const tieneClienteVinculado = !!selectedReservation?.cliente?.id;
  const clienteFieldLocked = (valorActual?: string): boolean =>
    tieneClienteVinculado && !!valorActual && valorActual.trim().length > 0;
  const esEmpresa = !!selectedReservation?.cliente?.es_empresa;
  const nombreLocked = clienteFieldLocked(selectedReservation?.nombre);
  // En una empresa el apellido es la sucursal: vacía no es un dato faltante
  // que se complete desde la reserva, y el backend tampoco la tomaría.
  const titularBloqueado = {
    nombre: nombreLocked,
    apellido: esEmpresa || clienteFieldLocked(selectedReservation?.apellido),
  };
  const dniLocked = clienteFieldLocked(selectedReservation?.dni);
  const telefonoLocked = clienteFieldLocked(selectedReservation?.telefono1);
  const emailLocked = clienteFieldLocked(selectedReservation?.email1);
  const localidadLocked = clienteFieldLocked(selectedReservation?.localidad);
  const hasGuestInfo =
    !!formData.afiliado_nombre?.trim() ||
    !!formData.afiliado_apellido?.trim() ||
    !!formData.afiliado_dni?.trim();
  const requiresGuestInfo =
    selectedReservation?.huesped_info_status === "faltante" ||
    (!!selectedReservation?.cliente?.es_empresa &&
      selectedReservation.cliente.origen_reserva !== "booking" &&
      selectedReservation.cliente.origen_reserva !== "airbnb");
  const guestInfoMissing = requiresGuestInfo && !hasGuestInfo;

  const gridSpacing = isShortDesktop || isMobile ? 1 : 2;
  const textFieldPy = isShortDesktop || isMobile ? 0 : 1;
  const bedIconSize = isShortDesktop || isMobile ? "2rem" : "2.5rem";

  return (
    <Box
      component="form"
      noValidate
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
      {/* Pestaña de Información */}
      {activeTab === 0 && (
        isShortDesktop ? (
          <Grid container spacing={2} sx={{ flex: 1, alignContent: "flex-start" }}>
            {/* COLUMNA IZQUIERDA: datos editables md=8 */}
            <Grid item xs={12} md={8}>
              <Grid container spacing={1}>
                {/* Fila 1: Entrada, Salida, Departamento */}
                <Grid item xs={4}>
                  <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    adapterLocale={es}
                  >
                    <DatePicker
                      label="Entrada"
                      value={formData.entrada}
                      onChange={(date: Date | null) =>
                        handleDateChange(date, "entrada")
                      }
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "small",
                          error: !!errors.entrada,
                          helperText: errors.entrada,
                        },
                        popper: {
                          sx: {
                            zIndex: Z.datepicker,
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={4}>
                  <LocalizationProvider
                    dateAdapter={AdapterDateFns}
                    adapterLocale={es}
                  >
                    <DatePicker
                      label="Salida"
                      value={formData.salida}
                      onChange={(date: Date | null) =>
                        handleDateChange(date, "salida")
                      }
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "small",
                          error: !!errors.salida,
                          helperText: errors.salida,
                        },
                        popper: {
                          sx: {
                            zIndex: Z.datepicker,
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={4}>
                  {renderDepartamentoSelect(
                    formData,
                    errors,
                    zonas,
                    handleTextChange,
                    shouldShowDepartmentOption
                  )}
                </Grid>

                {/* Fila 2: titular (nombre y apellido, o razón social) y DNI/Código */}
                <NombreClienteFields
                  esEmpresa={esEmpresa}
                  nombre={formData.nombre || ""}
                  apellido={formData.apellido || ""}
                  onChange={handleTextChange}
                  bloqueado={titularBloqueado}
                  columnas={{ xs: 4 }}
                />
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label={getReservationIdentityLabel(
                      selectedReservation?.cliente?.origen_reserva,
                      false
                    )}
                    value={formData.dni}
                    onChange={handleTextChange}
                    name="dni"
                    required
                    disabled={dniLocked}
                  />
                </Grid>

                {/* Fila 3: Huéspedes + Cama + Noches */}
                <Grid item xs={4}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25 }}>
                      Huéspedes
                    </Typography>
                    <ToggleButtonGroup
                      value={Number(formData.cantidad_huespedes)}
                      exclusive
                      onChange={(_, value) => {
                        if (value !== null) {
                          setFormData((prev) => ({
                            ...prev,
                            cantidad_huespedes: value.toString(),
                          }));
                        }
                      }}
                      fullWidth
                      size="small"
                      sx={{
                        "& .MuiToggleButton-root": {
                          flex: 1,
                          py: 0,
                          px: 0,
                          fontSize: "0.8rem",
                          border: "1px solid",
                          borderColor: errors.cantidad_huespedes
                            ? "error.main"
                            : "divider",
                          height: "32px",
                          "&.Mui-selected": {
                            bgcolor: "primary.main",
                            color: "primary.contrastText",
                            "&:hover": {
                              bgcolor: "primary.dark",
                            },
                          },
                        },
                      }}
                    >
                      {[1, 2, 3, 4, 5, 6].map((num) => (
                        <ToggleButton key={num} value={num}>
                          {num}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                    {errors.cantidad_huespedes && (
                      <Typography variant="caption" color="error">
                        {errors.cantidad_huespedes}
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, height: "100%", pt: 2 }}>
                    <Tooltip title="Cama Doble">
                      <IconButton
                        onClick={() => {
                          const currentTipoCama = formData.tipo_cama || [];
                          const newTipoCama = currentTipoCama.includes("doble")
                            ? currentTipoCama.filter((tipo) => tipo !== "doble")
                            : [...currentTipoCama, "doble"];
                          setFormData((prev) => ({
                            ...prev,
                            tipo_cama: newTipoCama,
                          }));
                        }}
                        sx={{
                          color: formData.tipo_cama.includes("doble")
                            ? "primary.main"
                            : "action.disabled",
                          "&:hover": { color: "primary.main" },
                          "& .MuiSvgIcon-root": { fontSize: bedIconSize },
                        }}
                      >
                        <KingBedIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Camas Individuales">
                      <IconButton
                        onClick={() => {
                          const currentTipoCama = formData.tipo_cama || [];
                          const newTipoCama = currentTipoCama.includes("individual")
                            ? currentTipoCama.filter((tipo) => tipo !== "individual")
                            : [...currentTipoCama, "individual"];
                          setFormData((prev) => ({
                            ...prev,
                            tipo_cama: newTipoCama,
                          }));
                        }}
                        sx={{
                          color: formData.tipo_cama.includes("individual")
                            ? "primary.main"
                            : "action.disabled",
                          "&:hover": { color: "primary.main" },
                          "& .MuiSvgIcon-root": { fontSize: bedIconSize },
                        }}
                      >
                        <Box sx={{ display: "flex", gap: 0.25 }}>
                          <SingleBedIcon />
                          <SingleBedIcon />
                        </Box>
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1,
                      textAlign: "center",
                      bgcolor: theme.palette.background.paper,
                      borderColor: theme.palette.divider,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0.5,
                      height: "100%",
                    }}
                  >
                    <NightsStayIcon sx={{ fontSize: "1.1rem", color: "primary.main" }} />
                    <Typography variant="body2" fontWeight={600}>
                      {CalculationService.calcularNoches(
                        formData.entrada,
                        formData.salida
                      )}
                    </Typography>
                  </Paper>
                </Grid>

                {/* Fila 4: Pagado + Cargar total */}
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Pagado"
                    name="pago"
                    type="number"
                    value={formData.pago}
                    onChange={handleTextChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                    error={!!errors.pago}
                    helperText={errors.pago}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Button
                    variant={esPagado ? "outlined" : "contained"}
                    color={esPagado ? "success" : "primary"}
                    onClick={handleMarcarPagado}
                    disabled={
                      esPagado || parseMonetaryValue(formData.valor_total) <= 0
                    }
                    fullWidth
                    size="small"
                    sx={{
                      py: 0.85,
                      fontWeight: 600,
                      textTransform: "none",
                      fontSize: "0.82rem",
                      height: "40px",
                    }}
                  >
                    {esPagado ? "Total cargado" : "Cargar total"}
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            {/* COLUMNA DERECHA: Cards de resumen y opciones md=4 */}
            <Grid item xs={12} md={4}>
              <Stack spacing={1.5}>
                {/* Card Resumen financiero */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: theme.palette.background.paper,
                    borderColor: theme.palette.divider,
                  }}
                >
                  <Typography variant="subtitle2" fontWeight="bold" color="primary.main" fontSize="0.85rem" gutterBottom>
                    Resumen financiero
                  </Typography>
                  <Stack spacing={0.75}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Total:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        ${parseMonetaryValue(formData.valor_total).toLocaleString("es-AR")}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Pagado:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        ${parseMonetaryValue(formData.pago).toLocaleString("es-AR")}
                      </Typography>
                    </Stack>
                    <Divider />
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Saldo:</Typography>
                      {saldoPendiente === 0 ? (
                        <Typography variant="body2" color="success.main" fontWeight={600}>Pagado</Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          color={saldoPendiente > 0 ? "warning.main" : "success.main"}
                          fontWeight={600}
                        >
                          {saldoResumenLabel}
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </Paper>

                {/* Card Opciones especiales */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: theme.palette.background.paper,
                    borderColor: theme.palette.divider,
                  }}
                >
                  <Typography variant="subtitle2" fontWeight="bold" color="primary.main" fontSize="0.85rem" gutterBottom>
                    Opciones especiales
                  </Typography>
                  <Stack spacing={0.5}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography variant="body2" fontWeight={500} fontSize="0.82rem">
                          Salida tardía (+50% una noche)
                        </Typography>
                      </Box>
                      <Switch
                        checked={formData.late_checkout}
                        onChange={async (e) => {
                          const isChecked = e.target.checked;
                          setFormData((prev) => ({
                            ...prev,
                            late_checkout: isChecked,
                          }));
                          try {
                            const response = await api.post(
                              "/reservas/calcular-valor-total",
                              {
                                reserva_id: selectedReservation?.id,
                                late_checkout: isChecked,
                              }
                            );
                            setFormData((prev) => ({
                              ...prev,
                              valor_total: response.data.valor_total.toString(),
                            }));
                          } catch (error) {
                            logger.error("Error al recalcular valor total:", error);
                          }
                        }}
                        color="warning"
                        size="small"
                      />
                    </Box>
                    {formData.late_checkout && (
                      <Alert severity="info" sx={{ fontSize: "0.75rem", py: 0.25 }}>
                        Recargo del 50% sobre una noche por salida tardía.
                      </Alert>
                    )}
                    <Divider sx={{ my: 0.5 }} />
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <CleaningServicesIcon sx={{ fontSize: "1rem", color: "text.secondary" }} />
                        <Typography variant="body2" fontWeight={500} fontSize="0.82rem">
                          No generar limpiezas automáticas
                        </Typography>
                      </Box>
                      <Switch
                        checked={formData.sin_limpieza_automatica}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            sin_limpieza_automatica: e.target.checked,
                          }))
                        }
                        color="primary"
                        size="small"
                      />
                    </Box>
                    <Divider sx={{ my: 0.5 }} />
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <CleaningServicesIcon sx={{ fontSize: "1rem", color: "text.secondary" }} />
                        <Typography variant="body2" fontWeight={500} fontSize="0.82rem">
                          Limpiezas pactadas
                        </Typography>
                      </Box>
                      <Switch
                        checked={formData.usar_limpiezas_manuales}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          setFormData((prev) => ({
                            ...prev,
                            usar_limpiezas_manuales: isChecked,
                            dias_limpieza_pactados: isChecked
                              ? prev.dias_limpieza_pactados
                              : [],
                            dias_limpieza_recambio: isChecked
                              ? prev.dias_limpieza_recambio
                              : [],
                          }));
                        }}
                        color="primary"
                        size="small"
                      />
                    </Box>
                  </Stack>
                </Paper>

                {/* Card Limpiezas pactadas (expandible) */}
                {formData.usar_limpiezas_manuales && (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: theme.palette.background.paper,
                      borderColor: "primary.light",
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight="bold" color="primary.main" fontSize="0.85rem" gutterBottom>
                      Limpiezas pactadas
                    </Typography>
                    <Stack spacing={1}>
                      <Alert severity="info" sx={{ fontSize: "0.72rem", py: 0.25 }}>
                        Las fechas pactadas se agregan a las limpiezas automáticas.
                      </Alert>
                      <LocalizationProvider
                        dateAdapter={AdapterDateFns}
                        adapterLocale={es}
                      >
                        <DatePicker
                          label="Agregar fecha"
                          value={null}
                          onChange={(date: Date | null) => {
                            if (!date) return;
                            const fechaStr = formatDateKey(date);
                            const entradaDate = formData.entrada ? parseDateOnly(formData.entrada) : null;
                            const salidaDate = formData.salida ? parseDateOnly(formData.salida) : null;
                            if (entradaDate && salidaDate) {
                              if (date < entradaDate || date > salidaDate) return;
                            }
                            // El día de salida ya tiene su limpieza de checkout.
                            if (formData.salida && fechaStr === formatDateKey(formData.salida)) {
                              setErrorLimpiezaPactada(
                                "Ese día ya tiene limpieza por la salida de la reserva."
                              );
                              return;
                            }
                            setErrorLimpiezaPactada(null);
                            if (formData.dias_limpieza_pactados.includes(fechaStr)) return;
                            const nuevasFechas = [
                              ...formData.dias_limpieza_pactados,
                              fechaStr,
                            ].sort();
                            setFormData((prev) => ({
                              ...prev,
                              dias_limpieza_pactados: nuevasFechas,
                            }));
                          }}
                          minDate={formData.entrada || undefined}
                          maxDate={formData.salida || undefined}
                          slotProps={{
                            textField: { size: "small", fullWidth: true },
                            popper: { sx: { zIndex: Z.datepicker } },
                          }}
                        />
                      </LocalizationProvider>
                      {errorLimpiezaPactada && (
                        <Alert
                          severity="info"
                          onClose={() => setErrorLimpiezaPactada(null)}
                          sx={{ py: 0 }}
                        >
                          {errorLimpiezaPactada}
                        </Alert>
                      )}
                      <ListaLimpiezasPactadas
                        fechas={formData.dias_limpieza_pactados}
                        fechasConRecambio={formData.dias_limpieza_recambio}
                        fechasRegistradas={fechasPactadasRegistradas}
                        onQuitarFecha={(fecha) =>
                          setFormData((prev) => ({
                            ...prev,
                            dias_limpieza_pactados:
                              prev.dias_limpieza_pactados.filter((f) => f !== fecha),
                            // La marca deja de tener sentido si el día ya no
                            // tiene limpieza pactada.
                            dias_limpieza_recambio:
                              prev.dias_limpieza_recambio.filter((f) => f !== fecha),
                          }))
                        }
                        onAlternarRecambio={(fecha, marcado) =>
                          setFormData((prev) => ({
                            ...prev,
                            dias_limpieza_recambio: marcado
                              ? [...prev.dias_limpieza_recambio, fecha].sort()
                              : prev.dias_limpieza_recambio.filter((f) => f !== fecha),
                          }))
                        }
                      />
                    </Stack>
                  </Paper>
                )}

                {/* Card Dinero a favor (solo si existe) */}
                {(parseMonetaryValue(formData.ajuste_financiero) > 0 ||
                  parseMonetaryValue(formData.pago) >
                    parseMonetaryValue(formData.valor_total)) && (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: "grey.50",
                      borderColor: "grey.200",
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight="bold" color="primary.main" fontSize="0.85rem" gutterBottom>
                      Dinero a favor
                    </Typography>
                    <Stack spacing={0.75}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Sobrepago:</Typography>
                        <Typography variant="body2" fontWeight="bold">
                          ${Math.max(0, parseMonetaryValue(formData.pago) - parseMonetaryValue(formData.valor_total)).toLocaleString("es-AR")}
                        </Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Estado:</Typography>
                        <Typography
                          variant="body2"
                          color={formData.tipo_ajuste === "sobrepago" || formData.tipo_ajuste === "ninguno" ? "warning.main" : "success.main"}
                          fontWeight={600}
                        >
                          {formData.tipo_ajuste === "sobrepago" || formData.tipo_ajuste === "ninguno"
                            ? "Retenido"
                            : "Devuelto"}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.5}>
                        <Button
                          variant={formData.tipo_ajuste === "sobrepago" || formData.tipo_ajuste === "ninguno" ? "contained" : "outlined"}
                          size="small"
                          color="warning"
                          onClick={() => setFormData((prev) => ({ ...prev, tipo_ajuste: "sobrepago", ajuste_financiero: "0" }))}
                          sx={{ minWidth: "auto", px: 1, fontSize: "0.7rem" }}
                        >
                          Retener
                        </Button>
                        <Button
                          variant={formData.tipo_ajuste === "devuelto" ? "contained" : "outlined"}
                          size="small"
                          color="success"
                          onClick={() => setFormData((prev) => ({ ...prev, tipo_ajuste: "devuelto", ajuste_financiero: "0" }))}
                          sx={{ minWidth: "auto", px: 1, fontSize: "0.7rem" }}
                        >
                          Devolver
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                )}
              </Stack>
            </Grid>
          </Grid>
        ) : (
          <Grid
            container
            spacing={gridSpacing}
            sx={{ flex: 1, alignContent: "flex-start" }}
          >
            {/* Layout normal desktop/mobile: fechas */}
            <Grid item xs={6} sm={6}>
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={es}
              >
                <DatePicker
                  label="Entrada"
                  value={formData.entrada}
                  onChange={(date: Date | null) =>
                    handleDateChange(date, "entrada")
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      error: !!errors.entrada,
                      helperText: errors.entrada,
                    },
                    popper: {
                      sx: {
                        zIndex: Z.datepicker,
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={6} sm={6}>
              <LocalizationProvider
                dateAdapter={AdapterDateFns}
                adapterLocale={es}
              >
                <DatePicker
                  label="Salida"
                  value={formData.salida}
                  onChange={(date: Date | null) =>
                    handleDateChange(date, "salida")
                  }
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      error: !!errors.salida,
                      helperText: errors.salida,
                    },
                    popper: {
                      sx: {
                        zIndex: Z.datepicker,
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.2,
                  py: isMobile ? 0.75 : 1.2,
                  textAlign: "center",
                  bgcolor: theme.palette.background.paper,
                  borderColor: theme.palette.divider,
                }}
              >
                <NightsStayIcon
                  sx={{
                    mr: 1,
                    verticalAlign: "middle",
                    color: "primary.main",
                  }}
                />
                <Typography variant="body1" component="span">
                  {CalculationService.calcularNoches(
                    formData.entrada,
                    formData.salida
                  )}
                </Typography>
              </Paper>
            </Grid>

            <NombreClienteFields
              esEmpresa={esEmpresa}
              nombre={formData.nombre || ""}
              apellido={formData.apellido || ""}
              onChange={handleTextChange}
              bloqueado={titularBloqueado}
              columnas={{ xs: 6 }}
              textFieldSx={{ px: { xs: 0, sm: 1 }, py: textFieldPy }}
            />

            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label={getReservationIdentityLabel(
                  selectedReservation?.cliente?.origen_reserva,
                  false
                )}
                value={formData.dni}
                onChange={handleTextChange}
                name="dni"
                required
                disabled={dniLocked}
                sx={{ px: { xs: 0, sm: 1 }, py: textFieldPy }}
              />
            </Grid>

            <Grid
              item
              xs={6}
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: isMobile ? 1 : 2,
              }}
            >
              <Tooltip title="Cama Doble">
                <IconButton
                  onClick={() => {
                    const currentTipoCama = formData.tipo_cama || [];
                    const newTipoCama = currentTipoCama.includes("doble")
                      ? currentTipoCama.filter((tipo) => tipo !== "doble")
                      : [...currentTipoCama, "doble"];
                    setFormData((prev) => ({
                      ...prev,
                      tipo_cama: newTipoCama,
                    }));
                  }}
                  sx={{
                    color: formData.tipo_cama.includes("doble")
                      ? "primary.main"
                      : "action.disabled",
                    "&:hover": {
                      color: "primary.main",
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: bedIconSize,
                    },
                  }}
                >
                  <KingBedIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Camas Individuales">
                <IconButton
                  onClick={() => {
                    const currentTipoCama = formData.tipo_cama || [];
                    const newTipoCama = currentTipoCama.includes("individual")
                      ? currentTipoCama.filter((tipo) => tipo !== "individual")
                      : [...currentTipoCama, "individual"];
                    setFormData((prev) => ({
                      ...prev,
                      tipo_cama: newTipoCama,
                    }));
                  }}
                  sx={{
                    color: formData.tipo_cama.includes("individual")
                      ? "primary.main"
                      : "action.disabled",
                    "&:hover": {
                      color: "primary.main",
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: bedIconSize,
                    },
                  }}
                >
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <SingleBedIcon />
                    <SingleBedIcon />
                  </Box>
                </IconButton>
              </Tooltip>
            </Grid>

            <Grid item xs={6}>
              <FormControl
                fullWidth
                error={!!errors.departamento}
                required
                size="small"
                sx={{
                  px: { xs: 0, sm: 0.5 },
                  py: { xs: 0, sm: 0.5 },
                }}
              >
                <InputLabel id="departamento-label">Departamento</InputLabel>
                <Select
                  labelId="departamento-label"
                  name="departamento"
                  value={formData.departamento}
                  onChange={handleTextChange}
                  label="Departamento"
                  startAdornment={
                    <InputAdornment position="start">
                      <ApartmentIcon sx={{ color: "primary.main" }} />
                    </InputAdornment>
                  }
                  MenuProps={{
                    sx: {
                      zIndex: Z.popover,
                    },
                  }}
                >
                  {zonas && zonas.length > 0
                    ? zonas.map((sector: Sector) => {
                      const departamentosVisibles = sector.departamentos.filter(
                        shouldShowDepartmentOption
                      );

                      if (departamentosVisibles.length === 0) return [];

                      return [
                        <MenuItem
                          key={sector.id}
                          disabled
                          divider
                          sx={{
                            bgcolor: "action.hover",
                            fontWeight: "bold",
                            color: "primary.main",
                          }}
                        >
                          Sector {sector.nombre}
                        </MenuItem>,
                        ...departamentosVisibles.map((depto: string) => (
                          <MenuItem key={depto} value={depto} sx={{ pl: 4 }}>
                            {depto}
                          </MenuItem>
                        )),
                      ];
                    })
                    : [
                        "01",
                        "02",
                        "03",
                        "04",
                        "05",
                        "06",
                        "07",
                        "08",
                        "09",
                        "10",
                        "11",
                        "12A",
                        "12B",
                        "14",
                        "15",
                        "16",
                        "17",
                        "18",
                        "19",
                        "20",
                        "21",
                        "22",
                        "23",
                        "24",
                        "25",
                        "26",
                        "27",
                      ].filter(shouldShowDepartmentOption).map((depto) => (
                        <MenuItem key={depto} value={depto}>
                          {depto}
                        </MenuItem>
                      ))}
                </Select>
                {errors.departamento && (
                  <FormHelperText>{errors.departamento}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Cantidad de huéspedes
                </Typography>
                <ToggleButtonGroup
                  value={Number(formData.cantidad_huespedes)}
                  exclusive
                  onChange={(_, value) => {
                    if (value !== null) {
                      setFormData((prev) => ({
                        ...prev,
                        cantidad_huespedes: value.toString(),
                      }));
                    }
                  }}
                  fullWidth
                  size="small"
                  sx={{
                    "& .MuiToggleButton-root": {
                      flex: 1,
                      py: isMobile ? 0 : 0.25,
                      px: isMobile ? 0 : 0.25,
                      fontSize: isMobile ? "0.8rem" : "0.85rem",
                      border: "1px solid",
                      borderColor: errors.cantidad_huespedes
                        ? "error.main"
                        : "divider",
                      height: "32px",
                      "&.Mui-selected": {
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                        "&:hover": {
                          bgcolor: "primary.dark",
                        },
                      },
                    },
                  }}
                >
                  {[1, 2, 3, 4, 5, 6].map((num) => (
                    <ToggleButton key={num} value={num}>
                      {num}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                {errors.cantidad_huespedes && (
                  <Typography variant="caption" color="error">
                    {errors.cantidad_huespedes}
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                label="Pagado"
                name="pago"
                type="number"
                value={formData.pago}
                onChange={handleTextChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                error={!!errors.pago}
                helperText={errors.pago}
              />
            </Grid>

            <Grid item xs={6}>
              <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
                <Button
                  variant={esPagado ? "outlined" : "contained"}
                  color={esPagado ? "success" : "primary"}
                  onClick={handleMarcarPagado}
                  disabled={
                    esPagado || parseMonetaryValue(formData.valor_total) <= 0
                  }
                  fullWidth
                  size="small"
                  sx={{
                    py: isMobile ? 0.8 : 1,
                    fontWeight: 600,
                    textTransform: "none",
                    height: isMobile ? "40px" : "auto",
                  }}
                >
                  {esPagado ? "Total cargado" : isMobile ? "Cargar total" : "Cargar total pagado"}
                </Button>
              </Box>
            </Grid>

            {/* Contenedor agrupado para checkboxes */}
            <Grid item xs={12}>
              <Stack spacing={0.5}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.late_checkout}
                      onChange={async (e) => {
                        const isChecked = e.target.checked;
                        setFormData((prev) => ({
                          ...prev,
                          late_checkout: isChecked,
                        }));
                        try {
                          const response = await api.post(
                            "/reservas/calcular-valor-total",
                            {
                              reserva_id: selectedReservation?.id,
                              late_checkout: isChecked,
                            }
                          );
                          setFormData((prev) => ({
                            ...prev,
                            valor_total: response.data.valor_total.toString(),
                          }));
                        } catch (error) {
                          logger.error("Error al recalcular valor total:", error);
                        }
                      }}
                      color="warning"
                    />
                  }
                  label="Late Check-out (+50% una noche)"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.9rem",
                      fontWeight: 500,
                    },
                    "& .MuiCheckbox-root": {
                      py: isMobile ? 0.25 : 0.5,
                    },
                  }}
                />
                {formData.late_checkout && (
                  <Alert severity="info" sx={{ ml: 4.5, fontSize: "0.85rem" }}>
                    Se aplicará un recargo del 50% del precio de una noche por
                    salida tardía.
                  </Alert>
                )}

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.sin_limpieza_automatica}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          sin_limpieza_automatica: e.target.checked,
                        }))
                      }
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CleaningServicesIcon sx={{ fontSize: "1.2rem" }} />
                      <Typography variant="body2" fontWeight={500}>
                        No generar limpiezas automáticas
                      </Typography>
                    </Box>
                  }
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.9rem",
                    },
                    "& .MuiCheckbox-root": {
                      py: isMobile ? 0.25 : 0.5,
                    },
                  }}
                />

                {isMobile ? (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.usar_limpiezas_manuales}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          setFormData((prev) => ({
                            ...prev,
                            usar_limpiezas_manuales: isChecked,
                            dias_limpieza_pactados: isChecked
                              ? prev.dias_limpieza_pactados
                              : [],
                          }));
                        }}
                        color="primary"
                      />
                    }
                    label="Agregar limpiezas pactadas"
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        fontSize: "0.9rem",
                        fontWeight: 500,
                      },
                      "& .MuiCheckbox-root": {
                        py: 0.25,
                      },
                    }}
                  />
                ) : (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.usar_limpiezas_manuales}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          setFormData((prev) => ({
                            ...prev,
                            usar_limpiezas_manuales: isChecked,
                            dias_limpieza_pactados: isChecked
                              ? prev.dias_limpieza_pactados
                              : [],
                          }));
                        }}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <CleaningServicesIcon sx={{ fontSize: "1.2rem" }} />
                        <Typography variant="body2" fontWeight={500}>
                          Agregar limpiezas pactadas
                        </Typography>
                      </Box>
                    }
                    sx={{
                      "& .MuiFormControlLabel-label": {
                        fontSize: "0.9rem",
                      },
                    }}
                  />
                )}
              </Stack>

              {formData.usar_limpiezas_manuales && (
                <Box sx={{ mt: 2, ml: isMobile ? 0 : 0 }}>
                  {isMobile ? (
                    <Box sx={{ ml: 4.5 }}>
                      <Alert severity="info" sx={{ mb: 2, fontSize: "0.85rem" }}>
                        <Typography variant="body2">
                          Las fechas pactadas se agregan a las limpiezas automáticas.
                          La limpieza de salida se mantiene. Las fechas
                          elegidas se respetan tal cual (puedes elegir domingo si lo necesitas).
                        </Typography>
                      </Alert>
                      <LocalizationProvider
                        dateAdapter={AdapterDateFns}
                        adapterLocale={es}
                      >
                        <DatePicker
                          label="Fecha de limpieza"
                          value={null}
                          onChange={(date: Date | null) => {
                            if (!date) return;
                            const fechaStr = formatDateKey(date);
                            const entradaDate = formData.entrada
                              ? parseDateOnly(formData.entrada)
                              : null;
                            const salidaDate = formData.salida
                              ? parseDateOnly(formData.salida)
                              : null;
                            if (entradaDate && salidaDate) {
                              if (date < entradaDate || date > salidaDate) {
                                logger.warn(
                                  `Fecha ${fechaStr} fuera del rango de la reserva`
                                );
                                return;
                              }
                            }
                            // El día de salida ya tiene su limpieza de checkout.
                            if (
                              formData.salida &&
                              fechaStr === formatDateKey(formData.salida)
                            ) {
                              setErrorLimpiezaPactada(
                                "Ese día ya tiene limpieza por la salida de la reserva."
                              );
                              return;
                            }
                            setErrorLimpiezaPactada(null);
                            if (
                              formData.dias_limpieza_pactados.includes(fechaStr)
                            ) {
                              logger.warn(`Fecha ${fechaStr} ya existe`);
                              return;
                            }
                            const nuevasFechas = [
                              ...formData.dias_limpieza_pactados,
                              fechaStr,
                            ].sort();
                            setFormData((prev) => ({
                              ...prev,
                              dias_limpieza_pactados: nuevasFechas,
                            }));
                          }}
                          minDate={formData.entrada || undefined}
                          maxDate={formData.salida || undefined}
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                            },
                            popper: {
                              sx: { zIndex: Z.datepicker },
                            },
                          }}
                        />
                      </LocalizationProvider>
                      {errorLimpiezaPactada && (
                        <Alert
                          severity="info"
                          onClose={() => setErrorLimpiezaPactada(null)}
                          sx={{ py: 0, mt: 1 }}
                        >
                          {errorLimpiezaPactada}
                        </Alert>
                      )}
                      <ListaLimpiezasPactadas
                        fechas={formData.dias_limpieza_pactados}
                        fechasConRecambio={formData.dias_limpieza_recambio}
                        fechasRegistradas={fechasPactadasRegistradas}
                        onQuitarFecha={(fecha) =>
                          setFormData((prev) => ({
                            ...prev,
                            dias_limpieza_pactados:
                              prev.dias_limpieza_pactados.filter((f) => f !== fecha),
                            // La marca deja de tener sentido si el día ya no
                            // tiene limpieza pactada.
                            dias_limpieza_recambio:
                              prev.dias_limpieza_recambio.filter((f) => f !== fecha),
                          }))
                        }
                        onAlternarRecambio={(fecha, marcado) =>
                          setFormData((prev) => ({
                            ...prev,
                            dias_limpieza_recambio: marcado
                              ? [...prev.dias_limpieza_recambio, fecha].sort()
                              : prev.dias_limpieza_recambio.filter((f) => f !== fecha),
                          }))
                        }
                      />
                    </Box>
                  ) : (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: theme.palette.background.paper,
                        borderColor: theme.palette.divider,
                      }}
                    >
                      <Stack spacing={2}>
                        <Alert severity="info" sx={{ mb: 2, fontSize: "0.85rem" }}>
                          <Typography variant="body2">
                            Las fechas pactadas se agregan a las limpiezas automáticas.
                            La limpieza de salida se mantiene. Las fechas
                            elegidas se respetan tal cual (puedes elegir domingo si lo necesitas).
                          </Typography>
                        </Alert>
                        <LocalizationProvider
                          dateAdapter={AdapterDateFns}
                          adapterLocale={es}
                        >
                          <DatePicker
                            label="Fecha de limpieza"
                            value={null}
                            onChange={(date: Date | null) => {
                              if (!date) return;
                              const fechaStr = formatDateKey(date);
                              const entradaDate = formData.entrada
                                ? parseDateOnly(formData.entrada)
                                : null;
                              const salidaDate = formData.salida
                                ? parseDateOnly(formData.salida)
                                : null;
                              if (entradaDate && salidaDate) {
                                if (date < entradaDate || date > salidaDate) {
                                  logger.warn(
                                    `Fecha ${fechaStr} fuera del rango de la reserva`
                                  );
                                  return;
                                }
                              }
                              // El día de salida ya tiene su limpieza de checkout.
                              if (
                                formData.salida &&
                                fechaStr === formatDateKey(formData.salida)
                              ) {
                                setErrorLimpiezaPactada(
                                  "Ese día ya tiene limpieza por la salida de la reserva."
                                );
                                return;
                              }
                              setErrorLimpiezaPactada(null);
                              if (
                                formData.dias_limpieza_pactados.includes(fechaStr)
                              ) {
                                logger.warn(`Fecha ${fechaStr} ya existe`);
                                return;
                              }
                              const nuevasFechas = [
                                ...formData.dias_limpieza_pactados,
                                fechaStr,
                              ].sort();
                              setFormData((prev) => ({
                                ...prev,
                                dias_limpieza_pactados: nuevasFechas,
                              }));
                            }}
                            minDate={formData.entrada || undefined}
                            maxDate={formData.salida || undefined}
                            slotProps={{
                              textField: {
                                size: "small",
                                fullWidth: true,
                              },
                              popper: {
                                sx: { zIndex: Z.datepicker },
                              },
                            }}
                          />
                        </LocalizationProvider>
                      {errorLimpiezaPactada && (
                        <Alert
                          severity="info"
                          onClose={() => setErrorLimpiezaPactada(null)}
                          sx={{ py: 0, mt: 1 }}
                        >
                          {errorLimpiezaPactada}
                        </Alert>
                      )}
                      <ListaLimpiezasPactadas
                        fechas={formData.dias_limpieza_pactados}
                        fechasConRecambio={formData.dias_limpieza_recambio}
                        fechasRegistradas={fechasPactadasRegistradas}
                        onQuitarFecha={(fecha) =>
                          setFormData((prev) => ({
                            ...prev,
                            dias_limpieza_pactados:
                              prev.dias_limpieza_pactados.filter((f) => f !== fecha),
                            // La marca deja de tener sentido si el día ya no
                            // tiene limpieza pactada.
                            dias_limpieza_recambio:
                              prev.dias_limpieza_recambio.filter((f) => f !== fecha),
                          }))
                        }
                        onAlternarRecambio={(fecha, marcado) =>
                          setFormData((prev) => ({
                            ...prev,
                            dias_limpieza_recambio: marcado
                              ? [...prev.dias_limpieza_recambio, fecha].sort()
                              : prev.dias_limpieza_recambio.filter((f) => f !== fecha),
                          }))
                        }
                      />
                      </Stack>
                    </Paper>
                  )}
                </Box>
              )}
            </Grid>

            {/* Layout de 2 columnas independientes */}
            <Box
              display="flex"
              justifyContent="center"
              width="100%"
              sx={{
                px: isMobile ? 0 : 2,
                py: isMobile ? 0.25 : 1,
              }}
            >
              <Grid
                container
                spacing={isMobile ? 1 : 3}
                sx={{
                  maxWidth: "900px",
                  width: "100%",
                }}
              >
                <Grid item xs={12} md={6}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: isMobile ? 1.25 : 2,
                      borderRadius: 2,
                      bgcolor: theme.palette.background.paper,
                      borderColor: theme.palette.divider,
                      height: "100%",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      transition: "box-shadow 0.2s ease-in-out",
                      "&:hover": {
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      },
                    }}
                  >
                    <Stack
                      spacing={isMobile ? 0.75 : 1.5}
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        color="primary.main"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                          Resumen financiero
                      </Typography>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography variant="body2" color="text.secondary">
                          Valor total:
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          $
                          {parseMonetaryValue(
                            formData.valor_total
                          ).toLocaleString("es-AR")}
                        </Typography>
                      </Stack>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography variant="body2" color="text.secondary">
                          Pagado:
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          $
                          {parseMonetaryValue(formData.pago).toLocaleString(
                            "es-AR"
                          )}
                        </Typography>
                      </Stack>
                      <Divider sx={{ my: 0.5 }} />
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography variant="body2" color="text.secondary">
                          Saldo:
                        </Typography>
                        {saldoPendiente === 0 ? (
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                          >
                            <Typography
                              variant="body2"
                              color="success.main"
                              sx={{ fontWeight: 600 }}
                            >
                              Pagado
                            </Typography>
                          </Stack>
                        ) : (
                          <Typography
                            variant="body2"
                            color={
                              saldoPendiente > 0 ? "warning.main" : "success.main"
                            }
                            sx={{ fontWeight: 600 }}
                          >
                            {saldoResumenLabel}
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                  </Paper>
                </Grid>
                {(parseMonetaryValue(formData.ajuste_financiero) > 0 ||
                  parseMonetaryValue(formData.pago) >
                      parseMonetaryValue(formData.valor_total)) && (
                  <Grid item xs={12} md={6}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        bgcolor: "grey.50",
                        borderColor: "grey.200",
                        height: "100%",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        transition: "box-shadow 0.2s ease-in-out",
                        "&:hover": {
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        },
                      }}
                    >
                      <Stack
                        spacing={1.5}
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          color="primary.main"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          Dinero a favor
                        </Typography>
                        <Stack spacing={1}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography variant="body2" color="text.secondary">
                              Sobrepago:
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              $
                              {Math.max(
                                0,
                                parseMonetaryValue(formData.pago) -
                                  parseMonetaryValue(formData.valor_total)
                              ).toLocaleString("es-AR")}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography variant="body2" color="text.secondary">
                              Estado actual:
                            </Typography>
                            <Typography
                              variant="body2"
                              color={
                                formData.tipo_ajuste === "sobrepago" ||
                                formData.tipo_ajuste === "ninguno"
                                  ? "warning.main"
                                  : "success.main"
                              }
                              sx={{ fontWeight: 600 }}
                            >
                              {formData.tipo_ajuste === "sobrepago" ||
                              formData.tipo_ajuste === "ninguno"
                                ? "Retenido"
                                : "Devuelto"}
                            </Typography>
                          </Stack>
                        </Stack>
                        <Stack direction="row" spacing={1}>
                          <Button
                            variant={
                              formData.tipo_ajuste === "sobrepago" ||
                              formData.tipo_ajuste === "ninguno"
                                ? "contained"
                                : "outlined"
                            }
                            size="small"
                            color="warning"
                            onClick={() => {
                              setFormData((prev) => ({
                                ...prev,
                                tipo_ajuste: "sobrepago",
                                ajuste_financiero: "0",
                              }));
                            }}
                            sx={{
                              minWidth: "auto",
                              px: 1.5,
                              fontSize: "0.75rem",
                            }}
                          >
                            Retener
                          </Button>
                          <Button
                            variant={
                              formData.tipo_ajuste === "devuelto"
                                ? "contained"
                                : "outlined"
                            }
                            size="small"
                            color="success"
                            onClick={() => {
                              setFormData((prev) => ({
                                ...prev,
                                tipo_ajuste: "devuelto",
                                ajuste_financiero: "0",
                              }));
                            }}
                            sx={{
                              minWidth: "auto",
                              px: 1.5,
                              fontSize: "0.75rem",
                            }}
                          >
                            Devolver
                          </Button>
                        </Stack>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontStyle: "italic", fontSize: "0.7rem" }}
                        >
                          Elegí si ese dinero queda retenido o fue devuelto.
                        </Typography>
                      </Stack>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>
        )
      )}

      {/* Pestaña de Contacto */}
      {activeTab === 1 && (
        <Grid
          container
          spacing={gridSpacing}
          sx={{ flex: 1, alignContent: "flex-start" }}
        >
          <Grid item xs={12}>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              sx={{
                color: "primary.main",
                display: "inline-block",
                mb: isShortDesktop ? 0.1 : 0.25,
                fontSize: isShortDesktop ? "0.85rem" : "0.95rem",
              }}
            >
              <LocationOnIcon sx={{ mr: 0.5, verticalAlign: "middle" }} />
              Información de contacto
            </Typography>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Teléfono principal"
              name="telefono1"
              value={formData.telefono1}
              onChange={handleTextChange}
              disabled={telefonoLocked}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon sx={{ color: "secondary.main" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              label="Email principal"
              name="email1"
              value={formData.email1}
              onChange={handleTextChange}
              disabled={emailLocked}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: "secondary.main" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Localidad"
              name="localidad"
              value={formData.localidad}
              onChange={handleTextChange}
              disabled={localidadLocked}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOnIcon sx={{ color: "secondary.main" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      )}

      {/* Pestaña de Huéspedes */}
      {activeTab === 2 && (
        <Grid
          container
          spacing={gridSpacing}
          sx={{ flex: 1, alignContent: "flex-start" }}
        >
          <Grid item xs={12}>
            <TabHuespedes
              formData={formData as unknown as NewReservationFormData}
              setFormData={
                setFormData as unknown as React.Dispatch<
                  React.SetStateAction<NewReservationFormData>
                >
              }
              setErrors={
                setErrors as unknown as React.Dispatch<
                  React.SetStateAction<{ [key: string]: string }>
                >
              }
              showTitle={false}
              guestInfoMissing={guestInfoMissing}
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default EditReservationForm;
